import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";

// The store definition is built lazily on first use rather than at module
// import time. Several modules that this store transitively depends on
// (services/cable, actionCableDebugStore, authStoreRefactor) import tenantStore
// back, forming import cycles. Vite's dev server tolerates those via live ESM
// bindings, but Vitest's transform does not: calling createBaseStoreRefactor()
// at module top level would run while baseStoreRefactor is still mid-init, so
// the export isn't ready yet ("createBaseStoreRefactor is not a function").
// Deferring the call until runtime guarantees every module has finished loading.
let tenantStoreDefinition = null;

function getTenantStoreDefinition() {
  if (tenantStoreDefinition) return tenantStoreDefinition;

  const tenantBaseStore = createBaseStoreRefactor({
    name: "tenant",
    endpoint: "/api/v1/public",
    infiniteLoading: false,
  });

  tenantStoreDefinition = defineStore("tenant", {
    state() {
      return {
        ...tenantBaseStore.state(),
        // Specific tenant state
        tenant: null,
      };
    },

    getters: {
      ...tenantBaseStore.getters,

      // Simple dot notation access - just use tenant.field directly in components
      isLoaded: (state) => !!state.tenant,
      tenantName: (state) => state.tenant?.name || "",
      tenantDisplayName: (state) =>
        state.tenant?.slug || state.tenant?.name || "",
      appIconUrl: (state) => state.tenant?.app_icon_url || "",
      tenantLogo: (state) => state.tenant?.app_icon_url || "", // Alias for logo
      tenantPrimaryColor: (state) => state.tenant?.primary_color || "#1976d2", // Default blue
      environment: (state) => state.tenant?.environment || "development",
      websocketUrl: (state) => state.tenant?.websocket_url || "",

      // Debug mode: enabled on development and staging (or when ?debug=1 / localStorage debug=1)
      isDebugMode: (state) => {
        const qs =
          typeof window !== "undefined"
            ? new URLSearchParams(window.location.search)
            : null;
        if (qs && qs.get("debug") === "1") return true;
        if (
          typeof window !== "undefined" &&
          localStorage.getItem("debug") === "1"
        )
          return true;
        const env =
          state.tenant?.environment ||
          import.meta.env.MODE ||
          (import.meta.env.DEV ? "development" : "production");
        return env === "development" || env === "staging";
      },
    },

    actions: {
      ...tenantBaseStore.actions,

      // Load tenant configuration from backend
      async load() {
        try {
          const result = await this.customApiCall(
            "GET",
            "/api/v1/public/tenants",
          );
          this.tenant = result;

          // Apply favicon if we have an app icon
          this.applyFavicon();

          return result;
        } catch (error) {
          console.error("Failed to load tenant config:", error);
          // Set a minimal fallback so app doesn't break
          this.tenant = {
            id: null,
            slug: "unknown",
            name: "Unknown Tenant",
            display_name: "Unknown",
            app_icon_url: "",
            environment: "development",
          };
          throw error;
        }
      },

      // Apply favicon from tenant app icon
      applyFavicon() {
        if (!this.tenant?.app_icon_url) return;

        const favicon =
          document.querySelector('link[rel="icon"]') ||
          document.querySelector('link[rel="shortcut icon"]');

        if (favicon) {
          favicon.href = this.tenant.app_icon_url;
        } else {
          const link = document.createElement("link");
          link.rel = "icon";
          link.href = this.tenant.app_icon_url;
          document.head.appendChild(link);
        }
      },

      // Update document title with tenant name
      updateDocumentTitle(pageTitle = "") {
        const baseTitle = this.tenantDisplayName;
        document.title = pageTitle ? `${pageTitle} - ${baseTitle}` : baseTitle;
      },
    },
  });

  return tenantStoreDefinition;
}

export function useTenantStore(...args) {
  return getTenantStoreDefinition()(...args);
}
